import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { useStripeContext } from '../../contexts/StripeContext';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import PlanSelector from './components/PlanSelector';
import BillingForm from './components/BillingForm';
import PaymentForm from './components/PaymentForm';
import OrderSummary from './components/OrderSummary';
import SuccessModal from './components/SuccessModal';
import { billingService } from '../../services/billingService';
import { stripePaymentService } from '../../services/stripePaymentService';

const SubscriptionCheckoutPaymentPortal = () => {
  const navigate = useNavigate();
  const { stripePromise, stripeOptions } = useStripeContext();
  
  const [step, setStep] = useState(1); // 1: Plan Selection, 2: Billing Info, 3: Payment
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Plan selection state
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [employeeCount, setEmployeeCount] = useState(10);
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or annual
  
  // Billing information state
  const [billingInfo, setBillingInfo] = useState({
    companyName: '',
    companyLegalName: '',
    taxIdNumber: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    province: '',
    postalCode: ''
  });
  
  // Payment state
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  
  // Success state
  const [showSuccess, setShowSuccess] = useState(false);
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    loadSubscriptionTiers();
  }, []);

  const loadSubscriptionTiers = async () => {
    try {
      setLoading(true);
      const tiers = await billingService?.getSubscriptionTiers();
      setSubscriptionTiers(tiers || []);
      
      // Pre-select Basic plan
      const basicPlan = tiers?.find(t => t?.tierName === 'basic');
      if (basicPlan) {
        setSelectedPlan(basicPlan);
      }
    } catch (err) {
      console.error('Error loading subscription tiers:', err);
      setError('Failed to load subscription plans. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!selectedPlan) return 0;
    
    let basePrice = selectedPlan?.monthlyPriceKinaMin || 0;
    
    // Calculate based on employee count
    if (employeeCount > selectedPlan?.minEmployees) {
      const priceRange = (selectedPlan?.monthlyPriceKinaMax || basePrice) - basePrice;
      const employeeRange = (selectedPlan?.maxEmployees || employeeCount) - selectedPlan?.minEmployees;
      const pricePerEmployee = priceRange / employeeRange;
      basePrice += pricePerEmployee * (employeeCount - selectedPlan?.minEmployees);
    }
    
    // Apply annual discount (15% off)
    if (billingCycle === 'annual') {
      return basePrice * 12 * 0.85;
    }
    
    return basePrice;
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setEmployeeCount(plan?.minEmployees || 10);
  };

  const handleNextStep = async () => {
    if (step === 1) {
      // Validate plan selection
      if (!selectedPlan) {
        setError('Please select a subscription plan');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      // Validate billing information
      const requiredFields = ['companyName', 'contactEmail', 'contactPhone', 'address'];
      const missingFields = requiredFields?.filter(field => !billingInfo?.[field]);
      
      if (missingFields?.length > 0) {
        setError('Please fill in all required fields');
        return;
      }
      
      // Create payment intent
      await createPaymentIntent();
    }
  };

  const createPaymentIntent = async () => {
    try {
      setProcessing(true);
      setError(null);
      
      const total = calculateTotal();
      
      const invoiceData = {
        amount: total,
        currency: 'PGK',
        description: `${selectedPlan?.displayName} - ${employeeCount} employees`,
        metadata: {
          planId: selectedPlan?.id,
          planName: selectedPlan?.tierName,
          employeeCount: employeeCount,
          billingCycle: billingCycle
        }
      };
      
      const customerInfo = {
        email: billingInfo?.contactEmail,
        name: billingInfo?.companyName,
        phone: billingInfo?.contactPhone,
        address: {
          line1: billingInfo?.address,
          city: billingInfo?.city,
          state: billingInfo?.province,
          postal_code: billingInfo?.postalCode,
          country: 'PG'
        },
        metadata: {
          taxIdNumber: billingInfo?.taxIdNumber,
          contactName: billingInfo?.contactName
        }
      };
      
      const result = await stripePaymentService?.createPaymentIntent(invoiceData, customerInfo);
      
      if (result?.clientSecret) {
        setClientSecret(result?.clientSecret);
        setPaymentIntentId(result?.paymentIntentId);
        setStep(3);
      } else {
        throw new Error('Failed to create payment intent');
      }
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError(err?.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      setProcessing(true);
      
      // Confirm payment and trigger account creation
      const result = await stripePaymentService?.confirmPayment(paymentIntent?.id);
      
      if (result?.transaction?.auto_account_created) {
        // Show success modal with credentials
        setCredentials({
          email: billingInfo?.contactEmail,
          password: result?.transaction?.generated_password,
          companyName: billingInfo?.companyName,
          plan: selectedPlan?.displayName
        });
        setShowSuccess(true);
      } else {
        // Payment successful but account creation pending
        setCredentials({
          email: billingInfo?.contactEmail,
          companyName: billingInfo?.companyName,
          plan: selectedPlan?.displayName,
          pending: true
        });
        setShowSuccess(true);
      }
    } catch (err) {
      console.error('Error confirming payment:', err);
      setError('Payment processed but account setup failed. Please contact support.');
    } finally {
      setProcessing(false);
    }
  };

  const handleBackStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setError(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading subscription plans...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {[1, 2, 3]?.map((stepNum) => (
              <React.Fragment key={stepNum}>
                <div className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    step >= stepNum
                      ? 'border-emerald-600 bg-emerald-600 text-white' :'border-gray-300 bg-white text-gray-400'
                  }`}>
                    {stepNum}
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    step >= stepNum ? 'text-emerald-600' : 'text-gray-400'
                  }`}>
                    {stepNum === 1 && 'Select Plan'}
                    {stepNum === 2 && 'Billing Info'}
                    {stepNum === 3 && 'Payment'}
                  </span>
                </div>
                {stepNum < 3 && (
                  <div className={`w-24 h-0.5 mx-4 ${
                    step > stepNum ? 'bg-emerald-600' : 'bg-gray-300'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {step === 1 && (
              <PlanSelector
                plans={subscriptionTiers}
                selectedPlan={selectedPlan}
                onSelectPlan={handlePlanSelection}
                employeeCount={employeeCount}
                onEmployeeCountChange={setEmployeeCount}
                billingCycle={billingCycle}
                onBillingCycleChange={setBillingCycle}
              />
            )}
            
            {step === 2 && (
              <BillingForm
                billingInfo={billingInfo}
                onBillingInfoChange={setBillingInfo}
              />
            )}
            
            {step === 3 && clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret, ...stripeOptions }}>
                <PaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={(err) => setError(err?.message)}
                  processing={processing}
                />
              </Elements>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <OrderSummary
              selectedPlan={selectedPlan}
              employeeCount={employeeCount}
              billingCycle={billingCycle}
              total={calculateTotal()}
            />
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleBackStep}
            disabled={step === 1 || processing}
          >
            Back
          </Button>
          
          {step < 3 && (
            <Button
              onClick={handleNextStep}
              loading={processing}
              disabled={processing}
            >
              {step === 2 ? 'Proceed to Payment' : 'Continue'}
            </Button>
          )}
        </div>
      </div>
      {/* Success Modal */}
      {showSuccess && credentials && (
        <SuccessModal
          credentials={credentials}
          onClose={() => {
            setShowSuccess(false);
            navigate('/main-dashboard-hub');
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionCheckoutPaymentPortal;